import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import { useParams } from 'react-router-dom';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend);

const currencyFmt = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
function formatCurrency(n) {
  try { return currencyFmt.format(n); } catch { return `$${Number(n).toFixed(2)}`; }
}

function formatYTDRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  const pad = (n) => String(n).padStart(2, '0');
  const toStr = (d) => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
  return { start: toStr(start), end: toStr(now) };
}

// Helper to compute cumulative (chain-linked) series from daily returns map keyed by date
function toCumulativeSeries(labels, dailyMap) {
  const out = [];
  let factor = 1;
  for (const d of labels) {
    const r = dailyMap[d];
    if (typeof r === 'number') {
      factor *= (1 + r);
      out.push(factor - 1);
    } else {
      out.push(out.length ? out[out.length - 1] : 0);
    }
  }
  return out;
}

function InvestedAssetDetail() {
  const { id: urlPortfolioId } = useParams();
  const [cronStatus, setCronStatus] = useState(null);
  const [cronLoading, setCronLoading] = useState(false);
  const [cronError, setCronError] = useState('');
  const [showCA, setShowCA] = useState(false);
  const [caSymbol, setCaSymbol] = useState('');
  const [caType, setCaType] = useState('REVERSE_SPLIT');
  const [caNumerator, setCaNumerator] = useState('1');
  const [caDenominator, setCaDenominator] = useState('10');
  const [caDate, setCaDate] = useState('');
  const [caNotes, setCaNotes] = useState('');
  const [caSubmitting, setCaSubmitting] = useState(false);
  const [returnsData, setReturnsData] = useState(null);
  const [positionsByDay, setPositionsByDay] = useState(null); // { [date]: [{symbol, mv, qty, account}] }
  const [loadingReturns, setLoadingReturns] = useState(false);
  const [loadingPositions, setLoadingPositions] = useState(false);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [txError, setTxError] = useState('');
  const [txSearch, setTxSearch] = useState('');
  const [txFilters, setTxFilters] = useState({
    date: '',
    account_number: '',
    transaction_type: '',
    symbol: '',
  });
  const [txSort, setTxSort] = useState({ key: 'timestamp', dir: 'desc' });
  const [posSearch, setPosSearch] = useState('');
  const [posFilters, setPosFilters] = useState({
    date: '',
    account: '',
    symbol: '',
  });
  const [posSort, setPosSort] = useState({ key: 'date', dir: 'desc' });
  const [error, setError] = useState('');

  const [portfolioId, setPortfolioId] = useState(urlPortfolioId ? Number(urlPortfolioId) : 1);
  const [portfolioName, setPortfolioName] = useState('');
  const [benchmarkId, setBenchmarkId] = useState(1);
  const [benchmarkName, setBenchmarkName] = useState('');
  const [showBenchmarkModal, setShowBenchmarkModal] = useState(false);
  const [bmModalData, setBmModalData] = useState({ name: '', constituents: [] });
  const [bmLoading, setBmLoading] = useState(false);
  const [bmError, setBmError] = useState('');
  const { start: ytdStart, end: ytdEnd } = formatYTDRange();
  const [startDate, setStartDate] = useState(ytdStart);
  const [endDate, setEndDate] = useState(ytdEnd);
  const [showCumulative, setShowCumulative] = useState(false);

  // Seeds manager state
  const [showSeedsMgr, setShowSeedsMgr] = useState(false);
  const [activeTab, setActiveTab] = useState('returns'); // 'returns' | 'mv' | 'contribution' | 'tx_pos'

  // Load names for portfolio and benchmark
  useEffect(() => {
    const fetchPortfolioName = async () => {
      try {
        const res = await axios.get(`/api/invested_assets/portfolios/${portfolioId}/`) ;
        let name = res.data.name;
        const isGeneric = !name || name === 'Heath Financial Investments' || name === `Portfolio ${portfolioId}`;
        if (isGeneric && res.data.accounts && res.data.accounts.length > 0) {
          const mainAcct = res.data.accounts[0];
          const brokerage = mainAcct.brokerage || '';
          const acctNum = mainAcct.account_number ? `(...${mainAcct.account_number.slice(-4)})` : '';
          const derivedName = [brokerage, acctNum].filter(Boolean).join(' ');
          if (derivedName) {
            name = derivedName;
          }
        }
        setPortfolioName(name || `Portfolio ${portfolioId}`);
      } catch (e) {
        console.error('Failed to fetch portfolio name', e);
        setPortfolioName(`Portfolio ${portfolioId}`);
      }
    };
    if (portfolioId) fetchPortfolioName();
  }, [portfolioId]);

  useEffect(() => {
    const fetchBenchmarkName = async () => {
      try {
        const res = await axios.get(`/api/invested_assets/benchmarks/${benchmarkId}/`) ;
        setBenchmarkName(res.data.name || `Benchmark ${benchmarkId}`);
      } catch (e) {
        console.error('Failed to fetch benchmark name', e);
        setBenchmarkName(`Benchmark ${benchmarkId}`);
      }
    };
    if (benchmarkId) fetchBenchmarkName();
  }, [benchmarkId]);

  // Cron status loader (callable)
  const reloadCronStatus = async () => {
    try {
      setCronLoading(true);
      setCronError('');
      const res = await axios.get('/api/invested_assets/cron_status/') ;
      setCronStatus(res.data);
    } catch (e) {
      console.error('Failed to load Schwab cron status', e);
      setCronError('Failed to load Schwab job status');
    } finally {
      setCronLoading(false);
    }
  };
  // Load Schwab cron status on mount
  useEffect(() => {
    reloadCronStatus();
    loadAccounts();
  }, []);
  const [seedsView, setSeedsView] = useState('list'); // 'list' | 'edit'
  const [seedsAccount, setSeedsAccount] = useState(''); // optional account filter and upsert target
  const [accounts, setAccounts] = useState([]); // fetched investment account options
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [viewMode, setViewMode] = useState('tiles'); // 'tiles' | 'details'
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [seedDates, setSeedDates] = useState([]); // [{date, count}]
  const [editSeedDate, setEditSeedDate] = useState(''); // YYYY-MM-DD
  const [editRows, setEditRows] = useState([]); // [{symbol, quantity}]

  // Auto-refresh dates when account selection changes while manager is open
  useEffect(() => {
    if (showSeedsMgr) {
      loadSeedDates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedsAccount, showSeedsMgr]);

  const loadAccounts = async () => {
    try {
      setAccountsLoading(true);
      const res = await axios.get('/api/invested_assets/accounts/list/') ;
      const arr = res.data?.accounts || [];
      setAccounts(arr);
    } catch (e) {
      console.error('Failed to load accounts list', e);
      setAccounts([]);
    } finally {
      setAccountsLoading(false);
    }
  };

  // ---------- Benchmark management ----------
  const openBenchmarkModal = async () => {
    try {
      setBmLoading(true);
      setBmError('');
      const res = await axios.get(`/api/invested_assets/benchmarks/${benchmarkId}/`) ;
      setBmModalData(res.data);
      setShowBenchmarkModal(true);
    } catch (e) {
      console.error('Failed to load benchmark details', e);
      setBmError('Failed to load benchmark details');
    } finally {
      setBmLoading(false);
    }
  };

  const handleBenchmarkSave = async () => {
    try {
      setBmLoading(true);
      setBmError('');
      const totalW = bmModalData.constituents.reduce((sum, c) => sum + parseFloat(c.weight || 0), 0);
      if (Math.abs(totalW - 1.0) > 1e-6) {
        setBmError(`Total weight must be 1.0 (currently ${totalW.toFixed(4)})`);
        setBmLoading(false);
        return;
      }
      await axios.put(`/api/invested_assets/benchmarks/${benchmarkId}/`, bmModalData);
      setBenchmarkName(bmModalData.name);
      setShowBenchmarkModal(false);
      // Refresh returns if benchmark changed
      fetchReturns({ allowAutoConstruct: false });
    } catch (e) {
      console.error('Failed to save benchmark', e);
      setBmError(e.response?.data?.error || 'Failed to save benchmark');
    } finally {
      setBmLoading(false);
    }
  };

  const updateBmConstituent = (idx, field, val) => {
    const newConsts = [...bmModalData.constituents];
    newConsts[idx] = { ...newConsts[idx], [field]: val };
    setBmModalData({ ...bmModalData, constituents: newConsts });
  };

  const addBmConstituent = () => {
    setBmModalData({
      ...bmModalData,
      constituents: [...bmModalData.constituents, { instrument: '', weight: 0 }]
    });
  };

  const removeBmConstituent = (idx) => {
    const newConsts = bmModalData.constituents.filter((_, i) => i !== idx);
    setBmModalData({ ...bmModalData, constituents: newConsts });
  };

  // ---------- Transactions fetch tied to date window ----------
  useEffect(() => {
    const run = async () => {
      try {
        setLoadingTransactions(true);
        setTxError('');
        const params = new URLSearchParams({ startDate, endDate });
        if (txSearch.trim()) params.set('search', txSearch.trim());
        const res = await axios.get(`/api/invested_assets/transactions/list/?${params.toString()}`);
        setTransactions(res.data?.results || []);
      } catch (e) {
        console.error('Failed to fetch transactions', e);
        setTxError('Failed to load transactions');
        setTransactions([]);
      } finally {
        setLoadingTransactions(false);
      }
    };
    run();
  }, [startDate, endDate]);

  // Derived filtered/sorted transactions (client-side)
  const visibleTransactions = useMemo(() => {
    let rows = transactions;
    // Global search filter
    const q = (txSearch || '').trim().toLowerCase();
    if (q) {
      rows = rows.filter(r => (
        (r.account_number || '').toLowerCase().includes(q) ||
        (r.transaction_type || '').toLowerCase().includes(q) ||
        (r.symbol || '').toLowerCase().includes(q)
      ));
    }
    // Column filters
    const f = txFilters;
    if (f.date) rows = rows.filter(r => (r.date || '').includes(f.date));
    if (f.account_number) rows = rows.filter(r => (r.account_number || '').toLowerCase().includes(f.account_number.toLowerCase()));
    if (f.transaction_type) rows = rows.filter(r => (r.transaction_type || '').toLowerCase().includes(f.transaction_type.toLowerCase()));
    if (f.symbol) rows = rows.filter(r => (r.symbol || '').toLowerCase().includes(f.symbol.toLowerCase()));
    // Sorting
    const { key, dir } = txSort;
    const mul = dir === 'desc' ? -1 : 1;
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
    rows = [...rows].sort((a, b) => {
      const va = a[key]; const vb = b[key];
      // numeric fallback
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * mul;
      return collator.compare(String(va ?? ''), String(vb ?? '')) * mul;
    });
    return rows;
  }, [transactions, txSearch, txFilters, txSort]);

  const setTxFilter = (k, v) => setTxFilters(prev => ({ ...prev, [k]: v }));
  const toggleSort = (k) => setTxSort(prev => ({ key: k, dir: (prev.key === k && prev.dir === 'asc') ? 'desc' : 'asc' }));

  const visiblePositions = useMemo(() => {
    if (!positionsByDay) return [];
    let rows = [];
    Object.entries(positionsByDay).forEach(([date, items]) => {
      const accountTotals = {};
      items.forEach(item => {
        rows.push({
          date,
          account: item.account,
          symbol: item.symbol,
          qty: item.qty,
          mv: item.mv,
          price: item.qty !== 0 ? item.mv / item.qty : 0
        });
        if (!accountTotals[item.account]) accountTotals[item.account] = 0;
        accountTotals[item.account] += (item.mv || 0);
      });
      // Insert TOTAL rows for each account on this date
      Object.entries(accountTotals).forEach(([acc, totalMV]) => {
        rows.push({
          date,
          account: acc,
          symbol: 'TOTAL',
          qty: null,
          mv: totalMV,
          price: null
        });
      });
    });

    // Global search
    const q = (posSearch || '').trim().toLowerCase();
    if (q) {
      rows = rows.filter(r => (
        (r.account || '').toLowerCase().includes(q) ||
        (r.symbol || '').toLowerCase().includes(q)
      ));
    }
    // Column filters
    const f = posFilters;
    if (f.date) rows = rows.filter(r => (r.date || '').includes(f.date));
    if (f.account) rows = rows.filter(r => (r.account || '').toLowerCase().includes(f.account.toLowerCase()));
    if (f.symbol) rows = rows.filter(r => (r.symbol || '').toLowerCase().includes(f.symbol.toLowerCase()));

    // Sorting
    const { key, dir } = posSort;
    const mul = dir === 'desc' ? -1 : 1;
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
    rows.sort((a, b) => {
      const va = a[key]; const vb = b[key];
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * mul;
      return collator.compare(String(va ?? ''), String(vb ?? '')) * mul;
    });
    return rows;
  }, [positionsByDay, posSearch, posFilters, posSort]);

  const setPosFilter = (k, v) => setPosFilters(prev => ({ ...prev, [k]: v }));
  const togglePosSort = (k) => setPosSort(prev => ({ key: k, dir: (prev.key === k && prev.dir === 'asc') ? 'desc' : 'asc' }));

  const openSeedsManager = async () => {
    setShowSeedsMgr(true);
    setSeedsView('list');
    await Promise.all([
      loadAccounts(),
      loadSeedDates(),
    ]);
  };
  const closeSeedsManager = () => {
    setShowSeedsMgr(false);
  };

  const renderCronLegend = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12, color: '#666' }}>
      <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#3CB371', borderRadius: 2, marginRight: 6 }}></span>OK</span>
      <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#DC143C', borderRadius: 2, marginRight: 6 }}></span>Error</span>
      <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#B0B0B0', borderRadius: 2, marginRight: 6 }}></span>Miss</span>
    </div>
  );

  const [intra, setIntra] = useState(null);
  const [intraError, setIntraError] = useState('');
  const [intraRefreshing, setIntraRefreshing] = useState(false);

  // Missed-jobs remediation state (UI to run afternoon/morning jobs for a date)
  const [jobDate, setJobDate] = useState(() => {
    // default to previous business day in local time
    const d = new Date();
    const wd = d.getDay();
    const back = wd === 1 ? 3 : (wd === 0 ? 2 : 1); // Mon->Fri, Sun->Fri, else -1 day
    d.setDate(d.getDate() - back);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
  });
  const [jobRunning, setJobRunning] = useState(false);
  const [jobMsg, setJobMsg] = useState('');

  const [remediateLoading, setRemediateLoading] = useState(false);

  const handleRemediate = async () => {
    if (!window.confirm('This will attempt to fetch missing daily data for the last 14 days and then reconstruct positions. This might take a minute. Continue?')) return;
    try {
      setRemediateLoading(true);
      const res = await axios.post('/api/invested_assets/jobs/remediate/',  {
        start: startDate,
        end: endDate
      });
      alert(res.data.message || 'Remediation triggered.');
      // Refresh UI data
      await reloadCronStatus();
      await constructPositions();
      await fetchReturns({ allowAutoConstruct: false });
    } catch (e) {
      console.error('Remediation failed', e);
      const errMsg = e.response?.data?.error || e.message || 'Remediation failed';
      alert('Failed: ' + errMsg);
    } finally {
      setRemediateLoading(false);
    }
  };


  // Shared loader for intraday snapshot (used by auto-poll and manual refresh button)
  const fetchIntradaySnapshot = async () => {
    try {
      const url = new URL('/api/invested_assets/intraday/snapshot/');
      if (portfolioId) url.searchParams.set('portfolio_id', String(portfolioId));
      if (benchmarkId) url.searchParams.set('benchmark_id', String(benchmarkId));
      const res = await axios.get(url.toString());
      if (res.status === 204) { setIntra(null); setIntraError(''); return; }
      setIntra(res.data);
      setIntraError('');
    } catch (e) {
      console.error('Failed to load intraday snapshot', e);
      setIntra(null);
      if (e?.response?.status === 401) setIntraError('Authorize Schwab to view intraday marks.');
      else setIntraError('Intraday snapshot unavailable.');
    }
  };

  // Manual refresh handler for the intraday badge
  const handleIntradayRefresh = async () => {
    if (intraRefreshing) return; // debounce
    setIntraRefreshing(true);
    try {
      await fetchIntradaySnapshot();
    } finally {
      setIntraRefreshing(false);
    }
  };

  // Load intraday snapshot on mount and poll every 90s
  useEffect(() => {
    let timer;
    const CT = { timeZone: 'America/Chicago', hour12: false };
    
    const shouldPoll = () => {
      const now = new Date();
      // Check if it's a weekday (Monday=1, Friday=5)
      const day = now.getDay();
      if (day === 0 || day === 6) return false;

      // Get current time in CT
      const formatter = new Intl.DateTimeFormat('en-US', {
        ...CT,
        hour: 'numeric',
        minute: 'numeric',
      });
      const parts = formatter.formatToParts(now);
      const hour = parseInt(parts.find(p => p.type === 'hour').value, 10);
      const minute = parseInt(parts.find(p => p.type === 'minute').value, 10);
      const totalMinutes = hour * 60 + minute;

      // Window is 08:30 to 15:00 CT (510 to 900 minutes)
      return totalMinutes >= 510 && totalMinutes <= 900;
    };

    const poll = () => {
      if (shouldPoll()) {
        fetchIntradaySnapshot();
      } else {
        // Clear data if we're outside the window and have data
        setIntra(prev => {
           if (prev !== null) return null;
           return prev;
        });
      }
    };

    poll();
    timer = setInterval(poll, 90000);
    return () => { if (timer) clearInterval(timer); };
  }, [portfolioId, benchmarkId]);

  const renderCronStrip = () => {
    if (cronLoading) return <div>Loading Schwab job status…</div>;
    if (cronError) return <div style={{ color: 'tomato' }}>{cronError}</div>;
    const days = cronStatus?.days || [];
    if (!days.length) return null;
    const box = (status) => {
      const color = status === 'o' ? '#3CB371' : status === 'e' ? '#DC143C' : '#B0B0B0';
      return { display: 'inline-block', width: 10, height: 10, background: color, borderRadius: 2 };
    };
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ fontSize: 12, color: '#333' }}>Schwab jobs (last 10 trading days):</div>
        {days.map((d, idx) => (
          <div key={idx} title={`${d.date}\nAfternoon: ${d.afternoon}\nMorning: ${d.morning}`}
               style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <div style={box(d.afternoon)}></div>
            <div style={box(d.morning)}></div>
          </div>
        ))}
        <div style={{ marginLeft: 8 }}>{renderCronLegend()}</div>
        <button 
          className="btn-primary" 
          style={{ marginLeft: 16, fontSize: 11, padding: '2px 8px' }}
          onClick={handleRemediate}
          disabled={remediateLoading}
        >
          {remediateLoading ? 'Remediating...' : 'Remediate Missing Data'}
        </button>
      </div>
    );
  };
  
  const loadSeedDates = async (accountOverride = null) => {
    try {
      const params = new URLSearchParams();
      const acct = (accountOverride ?? seedsAccount).trim();
      if (acct) params.append('account', acct);
      // Load a wide window and group by date on the client
      params.append('start', '2000-01-01');
      params.append('end', '2100-12-31');
      const res = await axios.get(`/api/invested_assets/seeds/list/?${params.toString()}`);
      const arr = res.data?.seeds || [];
      const byDate = {};
      for (const s of arr) {
        const d = s.seed_date;
        if (!byDate[d]) {
          byDate[d] = { 
            count: 0, 
            created_at: s.created_at, 
            updated_at: s.updated_at,
            brokerage: s.brokerage,
            account_type: s.account_type,
            account: s.account,
          };
        }
        byDate[d].count += 1;
        // Take the earliest created_at and latest updated_at for the group
        if (s.created_at && (!byDate[d].created_at || s.created_at < byDate[d].created_at)) {
          byDate[d].created_at = s.created_at;
        }
        if (s.updated_at && (!byDate[d].updated_at || s.updated_at > byDate[d].updated_at)) {
          byDate[d].updated_at = s.updated_at;
        }
      }
      const rows = Object.entries(byDate).map(([d, info]) => ({ 
        date: d, 
        count: info.count, 
        created_at: info.created_at, 
        updated_at: info.updated_at,
        brokerage: info.brokerage,
        account_type: info.account_type,
        account: info.account,
      })).sort((a,b)=>a.date.localeCompare(b.date));
      setSeedDates(rows);
    } catch (e) {
      console.error('Failed to load seeds list', e);
    }
  };
  const loadSeedsForDate = async (dateStr, accountOverride = null) => {
    try {
      const acctForLoad = (accountOverride ?? seedsAccount).trim();
      if (acctForLoad && acctForLoad !== seedsAccount) setSeedsAccount(acctForLoad);
      const params = new URLSearchParams({ start: dateStr, end: dateStr });
      if (acctForLoad) params.append('account', acctForLoad);
      const res = await axios.get(`/api/invested_assets/seeds/list/?${params.toString()}`);
      const arr = res.data?.seeds || [];
      // Build rows: ensure CASH exists (default 0) once per date/account
      const rows = [];
      let hasCash = false;
      for (const s of arr) {
        if (acctForLoad && s.account !== acctForLoad) continue;
        rows.push({ 
          symbol: s.symbol, 
          quantity: String(s.quantity),
          created_at: s.created_at,
          updated_at: s.updated_at
        });
        if ((s.symbol||'').toUpperCase() === 'CASH') hasCash = true;
      }
      if (!hasCash) rows.unshift({ symbol: 'CASH', quantity: '0' });
      setEditSeedDate(dateStr);
      setEditRows(rows);
      setSeedsView('edit');
    } catch (e) {
      console.error('Failed to load seeds for date', e);
    }
  };
  const addEditRow = () => setEditRows(rs => [...rs, { symbol: '', quantity: '' }]);
  const removeEditRow = (idx) => setEditRows(rs => rs.filter((_,i)=>i!==idx));
  const updateEditRow = (idx, field, value) => setEditRows(rs => rs.map((r,i)=> i===idx ? { ...r, [field]: value } : r));
  const deleteSeedsForDate = async (dateStr) => {
    if (!window.confirm(`Are you sure you want to delete all seeds for ${dateStr}?`)) return;
    try {
      const acct = seedsAccount.trim();
      const payload = { seed_date: dateStr };
      if (acct) payload.account = acct;
      
      await axios.post('/api/invested_assets/seeds/delete/', payload);
      await loadSeedDates(acct); // Refresh the list
    } catch (e) {
      console.error('Failed to delete seeds', e);
      alert('Failed to delete seeds.');
    }
  };
  const submitSeeds = async () => {
    try {
      if (!editSeedDate) return;
      const acct = seedsAccount.trim();
      if (!acct) { alert('Please enter an Account number to save seeds.'); return; }
      // Build payload; ensure CASH appears exactly once (use last value provided)
      const items = [];
      const seen = new Set();
      for (const r of editRows) {
        const sym = (r.symbol||'').toUpperCase().trim();
        if (!sym) continue;
        let qty = (r.quantity===''||r.quantity===null||r.quantity===undefined) ? '0' : String(r.quantity);
        if (seen.has(sym)) {
          // overwrite previous duplicate: remove earlier and keep last
          const idx = items.findIndex(x => x.symbol===sym);
          if (idx >= 0) items.splice(idx, 1);
        }
        seen.add(sym);
        items.push({ account: acct, symbol: sym, quantity: qty });
      }
      // Guarantee CASH present
      if (!seen.has('CASH')) items.unshift({ account: acct, symbol: 'CASH', quantity: '0' });
      await axios.post('/api/invested_assets/seeds/upsert/',  {
        seed_date: editSeedDate,
        seeds: items,
      });
      // After save, go back to list and refresh (explicitly pass current account to avoid stale state)
      await loadSeedDates(acct);
      setSeedsView('list');
    } catch (e) {
      console.error('Failed to upsert seeds', e);
      alert('Failed to save seeds.');
    }
  };

  // Construct positions for the current range
  const constructPositions = async () => {
    setLoadingPositions(true);
    try {
      const payload = {
        start: startDate,
        end: endDate,
        ignoreOptions: false, // include options by default
        ignoreSweeps: false,  // include sweep symbols (e.g., SWVXX/SNVXX) by default
        allowShorts: false,
      };
      await axios.post('/api/invested_assets/construct_positions/', payload);
    } catch (e) {
      console.error('Failed to construct positions', e);
      setError('Failed to construct positions.');
    } finally {
      setLoadingPositions(false);
    }
  };

  // Fetch portfolio vs benchmark returns (with auto-recovery if constructed positions missing)
  const fetchReturns = async (opts = { allowAutoConstruct: true }) => {
    setLoadingReturns(true);
    try {
      const url = `/api/invested_assets/returns/portfolio_vs_benchmark/?portfolio_id=${portfolioId}&benchmark_id=${benchmarkId}&start=${startDate}&end=${endDate}`;
      const res = await axios.get(url );
      setReturnsData(res.data);
      setError('');
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.error || 'Failed to fetch returns.';
      console.error('Failed to fetch returns', e);
      // Auto-recovery: if insufficient ConstructedPosition data, construct and retry once
      if (opts.allowAutoConstruct && status === 400 && String(msg).toLowerCase().includes('constructedposition')) {
        try {
          await constructPositions();
          const retry = await axios.get(`/api/invested_assets/returns/portfolio_vs_benchmark/?portfolio_id=${portfolioId}&benchmark_id=${benchmarkId}&start=${startDate}&end=${endDate}` );
          setReturnsData(retry.data);
          setError('');
          return;
        } catch (e2) {
          console.error('Auto-construct and retry failed', e2);
          setReturnsData(null);
          setError('Positions were constructed but returns still failed.');
        }
      } else {
        setError(msg);
        setReturnsData(null);
      }
    } finally {
      setLoadingReturns(false);
    }
  };

  // Auto-run returns fetch on initial load and whenever inputs change
  useEffect(() => {
    fetchReturns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioId, benchmarkId, startDate, endDate]);

  // Fetch per-day positions for tooltip when returnsData updates
  useEffect(() => {
    const run = async () => {
      if (!startDate || !endDate) return;
      setLoadingPositions(true);
      try {
        const params = new URLSearchParams({ start: startDate, end: endDate, excludeZero: '1' });
        const url = `/api/invested_assets/positions/by_day/?${params.toString()}`;
        const res = await axios.get(url );
        setPositionsByDay(res.data?.byDate || {});
      } catch (e) {
        console.error('Failed to fetch positions by day', e);
        setPositionsByDay(null);
      } finally {
        setLoadingPositions(false);
      }
    };
    run();
  }, [returnsData, startDate, endDate]);

  // OAuth helper
  const startAuthorization = async () => {
    try {
      const returnPath = window.location.pathname;
      const res = await axios.get(`/api/invested_assets/schwab/authorize/?return_path=${encodeURIComponent(returnPath)}`);
      window.location.href = res.data.auth_url;
    } catch (err) {
      console.error('Error getting auth URL', err);
      setError('Failed to start authorization.');
    }
  };

  // Render helpers for returns
  const dailyReturnsChart = () => {
    if (!returnsData) return null;
    const portDaily = returnsData.portfolio?.daily || [];
    const bmDaily = returnsData.benchmark?.daily || [];
    const baseLabels = Array.from(new Set([ ...portDaily.map(d=>d.date), ...bmDaily.map(d=>d.date) ])).sort();

    const portMap = Object.fromEntries(portDaily.map(d => [d.date, d.daily_return]));
    const bmMap = Object.fromEntries(bmDaily.map(d => [d.date, d.daily_return]));

    const isCum = showCumulative;
    const portSeriesBase = isCum ? toCumulativeSeries(baseLabels, portMap) : baseLabels.map(d => portMap[d] ?? null);
    const bmSeriesBase   = isCum ? toCumulativeSeries(baseLabels, bmMap)   : baseLabels.map(d => bmMap[d] ?? null);

    // If intraday snapshot is present (within trading window), extend the chart with today's temporary point
    let labels = baseLabels;
    let todayIdx = -1;
    let labelsExtended = false;
    const todayStr = intra?.businessDate || null;
    if (intra && todayStr) {
      if (!baseLabels.includes(todayStr)) {
        labels = [...baseLabels, todayStr];
        labelsExtended = true;
      }
      todayIdx = labels.indexOf(todayStr);
    }

    // Align base series to possibly-extended labels by padding null for the extra day
    const toExtended = (seriesBase) => labels.map((d, i) => (i < baseLabels.length ? seriesBase[i] : null));
    const portSeries = labelsExtended ? toExtended(portSeriesBase) : portSeriesBase;
    const bmSeries   = labelsExtended ? toExtended(bmSeriesBase)   : bmSeriesBase;

    // Build intraday overlay datasets (null everywhere except today)
    const datasets = [];
    const zeroLine = labels.map(() => 0);
    const data = { labels, datasets };

    datasets.push({
      label: '0% baseline',
      data: zeroLine,
      borderColor: 'rgba(220,220,220,0.6)',
      borderDash: [6, 6],
      borderWidth: 1,
      pointRadius: 0,
      pointHitRadius: 0,
      pointHoverRadius: 0,
      tension: 0,
      order: -100,
      fill: false,
      tooltip: { enabled: false },
    });

    datasets.push({ label: isCum ? 'Portfolio Cumulative Return' : 'Portfolio Daily Return', data: portSeries, borderColor: '#2b7', backgroundColor: 'rgba(34,187,119,0.2)', tension: 0.2 });
    datasets.push({ label: isCum ? 'Benchmark Cumulative Return' : 'Benchmark Daily Return', data: bmSeries, borderColor: '#f70', backgroundColor: 'rgba(255,119,0,0.2)', tension: 0.2 });

    if (intra && todayIdx >= 0) {
      // Portfolio intraday value
      const pIntraday = typeof intra?.portfolio?.returnPct === 'number' ? intra.portfolio.returnPct : null;
      // Benchmark intraday value may be null
      const bIntraday = typeof intra?.benchmark?.returnPct === 'number' ? intra.benchmark.returnPct : null;

      // Helper to compute cumulative extension given last cumulative value and intraday pct
      const extendCum = (lastVal, r) => {
        const lv = typeof lastVal === 'number' ? lastVal : 0;
        const rp = typeof r === 'number' ? r : 0;
        return (1 + lv) * (1 + rp) - 1;
      };

      // Determine plotted values for intraday overlays
      const lastPort = portSeries[baseLabels.length - 1] ?? null;
      const lastBench = bmSeries[baseLabels.length - 1] ?? null;
      const pPoint = isCum ? extendCum(lastPort, pIntraday) : pIntraday;
      const bPoint = isCum ? (bIntraday != null ? extendCum(lastBench, bIntraday) : null) : bIntraday;

      // Build sparse arrays with only today's index populated
      const mkSparse = (val) => labels.map((_, idx) => (idx === todayIdx ? val : null));

      if (pPoint != null) {
        datasets.push({
          label: isCum ? 'Portfolio (Intraday)' : 'Portfolio (Intraday)',
          data: mkSparse(pPoint),
          borderColor: '#2b7',
          backgroundColor: '#2b7',
          borderDash: [4, 4],
          borderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 5,
          spanGaps: true,
        });
      }
      if (bPoint != null) {
        datasets.push({
          label: isCum ? 'Benchmark (Intraday)' : 'Benchmark (Intraday)',
          data: mkSparse(bPoint),
          borderColor: '#f70',
          backgroundColor: '#f70',
          borderDash: [4, 4],
          borderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 5,
          spanGaps: true,
        });
      }
    }

    const pct = (v, digits=2) => `${(v*100).toFixed(digits)}%`;

    const options = {
      plugins: {
        tooltip: {
          callbacks: {
            label: (ctx) => {
              // In cumulative mode, we render custom lines in beforeBody and suppress default label
              if (isCum) return '';
              const val = ctx.parsed.y;
              if (val == null) return '';
              return `${ctx.dataset.label}: ${pct(val)}`;
            },
            beforeBody: (items) => {
              if (!isCum || !items || !items.length) return [];
              const idx = items[0].dataIndex;
              const p = portSeries[idx];
              const b = bmSeries[idx];
              if (p == null && b == null) return [];
              const lines = [];
              if (p != null) lines.push(`Portfolio: ${pct(p)}`);
              if (b != null) lines.push(`Benchmark: ${pct(b)}`);
              if (p != null && b != null) {
                const diff = p - b;
                const sign = diff > 0 ? '+' : '';
                lines.push(`Diff (P - B): ${sign}${pct(diff)}`);
              }
              return lines;
            },
          },
        },
        legend: { display: true },
      },
      scales: {
        y: {
          ticks: {
            callback: (value) => `${(value * 100).toFixed(0)}%`,
          },
        },
      },
    };

    return (
      <div style={{ marginTop: 16, position: 'relative' }}>
        {/* Toggle tethered to top-right of the chart container */}
        <div style={{ position: 'absolute', top: -6, right: 0, zIndex: 2, background: 'rgba(255,255,255,0.85)', padding: '4px 8px', borderRadius: 4 }}>
          <label style={{ fontSize: 12, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={showCumulative}
              onChange={(e) => setShowCumulative(e.target.checked)}
              style={{ marginRight: 6 }}
            />
            Show cumulative (chain-linked)
          </label>
        </div>
        <h3 style={{ marginRight: 160 }}>{isCum ? 'Cumulative (Chain-Linked) Returns: Portfolio vs. Benchmark' : 'Daily Returns: Portfolio vs. Benchmark'}</h3>
        <div style={{ height: 420 }}>
          <Line data={data} options={{ ...options, maintainAspectRatio: false }} />
        </div>
      </div>
    );
  };

  const portfolioMVFromReturnsChart = () => {
    if (!returnsData) return null;
    const mvSeries = returnsData.portfolio?.mv_series || [];
    if (!mvSeries.length) return null;
    const labels = mvSeries.map(x => x.date);
    const mvMap = Object.fromEntries(mvSeries.map(x => [x.date, x.mv]));
    const dataVals = mvSeries.map(x => x.mv);

    // Tooltip callbacks to show total MV and per-symbol list with right-justified values
    const options = {
      plugins: {
        tooltip: {
          enabled: true,
          bodyFont: { family: 'monospace' },
          callbacks: {
            // Keep the default title (date)
            beforeBody: (items) => {
              try {
                if (!items || !items.length) return [];
                const idx = items[0].dataIndex;
                const date = labels[idx];
                const total = mvMap[date] ?? null;
                if (total == null) return [];
                return [ `Total: ${formatCurrency(total)}`, '']; // blank line
              } catch { return []; }
            },
            label: (ctx) => {
              // We'll render nothing in the default label row to avoid duplicates
              return '';
            },
            afterBody: (items) => {
              try {
                if (!items || !items.length) return [];
                const idx = items[0].dataIndex;
                const date = labels[idx];
                const dayList = positionsByDay?.[date] || [];
                if (!dayList.length) return ['No positions'];
                // Compute right-justified values using padding in monospace
                const values = dayList.map(p => formatCurrency(p.mv));
                const maxLen = Math.max(...values.map(v => v.length), 0);
                const nfQtyInt = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });
                const nfQtyFrac = new Intl.NumberFormat(undefined, { maximumFractionDigits: 4 });
                function fmtQty(q) {
                  if (q == null) return '';
                  const isInt = Math.abs(q - Math.round(q)) < 1e-6;
                  return isInt ? nfQtyInt.format(Math.round(q)) : nfQtyFrac.format(q);
                }
                return dayList.map((p, i) => {
                  const val = values[i].padStart(maxLen, ' ');
                  const qty = p.qty;
                  const unitPrice = (p && typeof p.price === 'number') ? p.price : (qty ? (p.mv / qty) : null);
                  const extra = (qty && unitPrice) ? ` (${fmtQty(qty)} x ${formatCurrency(unitPrice)})` : '';
                  return `${p.symbol}  ${val}${extra}`;
                });
              } catch { return []; }
            },
          },
        },
      },
      maintainAspectRatio: false,
    };

    const data = {
      labels,
      datasets: [
        { label: 'Portfolio Market Value (Constructed/Summary)', data: dataVals, borderColor: '#06c', backgroundColor: 'rgba(0,102,204,0.2)', tension: 0.25 },
      ],
    };
    return (
      <div style={{ marginTop: 16 }}>
        <h3>Portfolio Market Value (Selected Period)</h3>
        <div style={{ height: 320 }}>
          <Line data={data} options={{ ...options, maintainAspectRatio: false }} />
        </div>
        {loadingPositions && <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>Loading position details for tooltips…</div>}
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 12 }}>
        {/* Left zone: Title + Intraday block to the right of title */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <h1 style={{ marginBottom: 4 }}>Invested Assets</h1>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 8 }}>
            <div>
              <label style={{ color: '#94a3b8', fontSize: 13 }}>Portfolio:&nbsp;</label>
              <span style={{ fontWeight: 600, color: '#e2e8f0', fontSize: 14 }}>{portfolioName || portfolioId}</span>
            </div>
            <div>
              <label style={{ color: '#94a3b8', fontSize: 13 }}>Benchmark:&nbsp;</label>
              <span style={{ fontWeight: 600, color: '#e2e8f0', fontSize: 14 }}>{benchmarkName || benchmarkId}</span>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6, margin: '6px 0 12px 0' }}>
            {/* Intraday badge */}
            {(() => {
              if (!intra && !intraError) return null;
              if (intraError) return <div style={{ color: 'tomato', fontSize: 12 }}>{intraError}</div>;
              const p = intra?.portfolio || {}; const b = intra?.benchmark || {};
              const pct = (p?.returnPct ?? 0) * 100;
              const val = p?.returnValue ?? 0;
              const bmPct = (b?.returnPct != null) ? (b.returnPct*100) : null;
              // Small circular refresh button
              const btnStyle = {
                width: 24,
                height: 24,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                border: '1px solid #94a3b8',
                background: '#f8fafc',
                color: '#0f172a',
                cursor: intraRefreshing ? 'default' : 'pointer',
                marginRight: 8,
              };
              const spinStyle = intraRefreshing ? { animation: 'spin 0.9s linear infinite' } : {};
              return (
                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <button
                    onClick={handleIntradayRefresh}
                    disabled={intraRefreshing}
                    title="Refresh intraday snapshot"
                    aria-label="Refresh intraday snapshot"
                    style={btnStyle}
                  >
                    {/* inline SVG refresh icon */}
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={spinStyle}>
                      <polyline points="23 4 23 10 17 10"></polyline>
                      <polyline points="1 20 1 14 7 14"></polyline>
                      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"></path>
                      <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14"></path>
                    </svg>
                  </button>
                  <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 12, rowGap: 2, alignItems: 'baseline', lineHeight: 1.1 }}>
                    {(() => {
                      const colorFor = (x) => (x > 0 ? '#3CB371' : x < 0 ? '#DC143C' : undefined);
                      const diff = (bmPct != null) ? (pct - bmPct) : null;
                      // Build timestamp string in CT
                      let asOfStr = '';
                      try {
                        const asOfIso = intra?.asOf;
                        if (asOfIso) {
                          const dt = new Date(asOfIso);
                          asOfStr = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Chicago', hour: 'numeric', minute: '2-digit', second: '2-digit' }).format(dt);
                        }
                      } catch (e) { /* ignore */ }
                      return (
                        <>
                          <div style={{ fontWeight: 600, fontSize: 22 }}>
                            Portfolio: <span style={{ color: colorFor(pct) }}>{pct.toFixed(2)}%</span>
                            {diff != null && (
                              <span style={{ marginLeft: 6, color: colorFor(diff) }}>({diff.toFixed(2)}%)</span>
                            )}
                            {' '}<span style={{ color: colorFor(val) }}>({formatCurrency(val)})</span>
                          </div>
                          <div style={{ fontSize: 12, color: '#666' }}>
                            {asOfStr}
                          </div>
                          <div style={{ fontSize: 18 }}>
                            Benchmark: {bmPct!=null ? (
                              <span style={{ color: (bmPct>0?'#3CB371':bmPct<0?'#DC143C':undefined) }}>{bmPct.toFixed(2)}%</span>
                            ) : '—'}
                          </div>
                          <div style={{ fontSize: 12, color: '#666' }}>
                            {intra?.priorCloseDate ? `(vs ${intra.priorCloseDate})` : ''}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                  {/* keyframes for spin animation */}
                  <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
                </div>
              );
            })()}
          </div>
        </div>
        {/* Right zone: Cron status strip anchored to top-right */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', marginLeft: 16 }}>
          {renderCronStrip()}
          {(() => {
            const sanity = returnsData?.portfolio?.sanity;
            if (!sanity) return null;
            const mismatches = sanity.mismatches || [];
            const posMis = sanity.position_mismatches || [];
            const seedMis = sanity.seed_mismatches || [];
            if (mismatches.length === 0 && posMis.length === 0 && seedMis.length === 0) return null;
            const countDates = mismatches.length;
            const countPos = posMis.length;
            const countSeed = seedMis.length;
            const exDate = (mismatches.length ? [...mismatches].sort((a, b) => (a.date > b.date ? -1 : a.date < b.date ? 1 : 0))[0] : null);
            const exPos = (posMis.length ? [...posMis].sort((a, b) => (a.date > b.date ? -1 : a.date < b.date ? 1 : 0))[0] : null);
            const exSeed = (seedMis.length ? [...seedMis].sort((a, b) => (a.date > b.date ? -1 : a.date < b.date ? 1 : 0))[0] : null);

            const SanityBanner = () => {
              const [expanded, setExpanded] = React.useState(false);
              const [seedExpanded, setSeedExpanded] = React.useState(false);

              let firstDate = exDate?.date || null;
              if (!firstDate && countPos > 0) {
                try {
                  const dates = Array.from(new Set(posMis.map(p => p.date))).sort((a,b)=> (a > b ? -1 : a < b ? 1 : 0));
                  firstDate = dates[0] || null;
                } catch { /* ignore */ }
              }
              const listForDate = (firstDate ? posMis.filter(p => p.date === firstDate) : []).slice();
              listForDate.sort((a, b) => (Math.abs(b.pct_diff || 0) - Math.abs(a.pct_diff || 0)));

              const seedDates = Array.from(new Set(seedMis.map(s => s.date))).sort((a,b)=> (a > b ? -1 : a < b ? 1 : 0));
              let firstSeedDate = seedDates[0] || null;
              const seedListForDate = (firstSeedDate ? seedMis.filter(s => s.date === firstSeedDate) : []).slice();
              seedListForDate.sort((a, b) => (Math.abs(b.pct_diff || 0) - Math.abs(a.pct_diff || 0)));

              const toggle = () => setExpanded(v => !v);
              const toggleSeed = () => setSeedExpanded(v => !v);

              return (
                <div style={{
                  padding: '6px 12px',
                  background: '#fff8e1',
                  border: '1px solid #ffcc80',
                  color: '#8a6d3b',
                  borderRadius: 4,
                  marginTop: 6,
                  maxWidth: 700,
                  fontSize: 12
                }}>
                  <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 16, justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <strong style={{ whiteSpace: 'nowrap' }}>Warning:</strong>
                      {countDates > 0 && (
                        <div style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4 }}>
                          <span>MV diff > {(sanity?.threshold*100||1).toFixed(0)}% ({countDates} d).</span>
                          {exDate && (
                            <span style={{ fontSize: 11, color: '#a07d42' }}>Ex: {exDate.date} diff {(exDate.pct_diff*100).toFixed(2)}%.</span>
                          )}
                        </div>
                      )}
                      {countPos > 0 && (
                        <div style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4 }}>
                          <span>{countPos} position mismatch.</span>
                          {exPos && (
                            <span style={{ fontSize: 11, color: '#a07d42' }}>Ex: {exPos.date} {exPos.symbol} diff {(exPos.pct_diff*100).toFixed(2)}%.</span>
                          )}
                        </div>
                      )}
                      {countSeed > 0 && (
                        <div style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4, color: '#b91c1c' }}>
                          <span>{countSeed} seed mismatch.</span>
                          {exSeed && (
                            <span style={{ fontSize: 11, color: '#f87171' }}>Ex: {exSeed.date} {exSeed.symbol} diff {(exSeed.pct_diff*100).toFixed(2)}%.</span>
                          )}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'nowrap', flexShrink: 0 }}>
                      {(firstDate && countPos > 0) && (
                        <button onClick={toggle} style={{ fontSize: 10, padding: '1px 4px', cursor: 'pointer' }}>
                          {expanded ? 'Hide Diffs' : `Show Schwab Diffs`}
                        </button>
                      )}
                      {(firstSeedDate && countSeed > 0) && (
                        <button onClick={toggleSeed} style={{ fontSize: 10, padding: '1px 4px', cursor: 'pointer', background: '#fee2e2', border: '1px solid #f87171' }}>
                          {seedExpanded ? 'Hide Seeds' : `Show Seed Diffs`}
                        </button>
                      )}
                    </div>
                  </div>

                  {expanded && firstDate && (
                    <div style={{ position: 'fixed', top: 100, right: 20, zIndex: 1000, background: '#fff', border: '1px solid #ffe0b2', borderRadius: 4, padding: 8, boxShadow: '0 4px 6px rgba(0,0,0,0.1)', maxWidth: '90vw', overflowX: 'auto' }}>
                      <div style={{ marginBottom: 6, fontWeight: 600, fontSize: 12 }}>Position mismatches on {firstDate} ({listForDate.length}):</div>
                      <div style={{ fontFamily: 'monospace', fontSize: 11 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '100px 80px 120px 120px 70px', gap: 8, paddingBottom: 6, borderBottom: '1px solid #eee' }}>
                          <div>Account</div>
                          <div>Symbol</div>
                          <div>Const MV</div>
                          <div>Schwab MV</div>
                          <div>Diff %</div>
                        </div>
                        {listForDate.map((p, idx) => (
                          <div key={`${p.account}-${p.symbol}-${idx}`} style={{ display: 'grid', gridTemplateColumns: '100px 80px 120px 120px 70px', gap: 8, padding: '4px 0', borderBottom: '1px solid #f7f1e0' }}>
                            <div>{p.account}</div>
                            <div>{p.symbol}</div>
                            <div>{formatCurrency(p.constructed_mv)}</div>
                            <div>{formatCurrency(p.schwab_mv)}</div>
                            <div>{(p.pct_diff*100).toFixed(2)}%</div>
                          </div>
                        ))}
                      </div>
                      <button onClick={toggle} style={{ marginTop: 8, fontSize: 11 }}>Close</button>
                    </div>
                  )}

                  {seedExpanded && firstSeedDate && (
                    <div style={{ position: 'fixed', top: 120, right: 40, zIndex: 1001, background: '#fff', border: '1px solid #fca5a5', borderRadius: 4, padding: 8, boxShadow: '0 4px 6px rgba(0,0,0,0.1)', maxWidth: '90vw', overflowX: 'auto' }}>
                      <div style={{ marginBottom: 6, fontWeight: 600, color: '#b91c1c', fontSize: 12 }}>Seed mismatches on {firstSeedDate} ({seedListForDate.length}):</div>
                      <div style={{ fontFamily: 'monospace', fontSize: 11 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '100px 60px 100px 100px 100px 70px', gap: 8, paddingBottom: 6, borderBottom: '1px solid #eee' }}>
                          <div>Account</div>
                          <div>Symbol</div>
                          <div>Calc Qty</div>
                          <div>Seed Qty</div>
                          <div>Calc MV</div>
                          <div>Diff %</div>
                        </div>
                        {seedListForDate.map((s, idx) => (
                          <div key={`${s.account}-${s.symbol}-${idx}`} style={{ display: 'grid', gridTemplateColumns: '100px 60px 100px 100px 100px 70px', gap: 8, padding: '4px 0', borderBottom: '1px solid #fee2e2' }}>
                            <div>{s.account}</div>
                            <div>{s.symbol}</div>
                            <div>{s.calc_qty.toFixed(2)}</div>
                            <div>{s.seed_qty.toFixed(2)}</div>
                            <div>{formatCurrency(s.calc_mv)}</div>
                            <div>{(s.pct_diff*100).toFixed(2)}%</div>
                          </div>
                        ))}
                      </div>
                      <button onClick={toggleSeed} style={{ marginTop: 8, fontSize: 11 }}>Close</button>
                    </div>
                  )}
                </div>
              );
            };

            return (<SanityBanner />);
          })()}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginTop: 12 }}>
        <div>
          <label>Start:&nbsp;</label>
          <input type="date" value={startDate} onChange={(e)=>setStartDate(e.target.value)} />
        </div>
        <div>
          <label>End:&nbsp;</label>
          <input type="date" value={endDate} onChange={(e)=>setEndDate(e.target.value)} />
        </div>
        <button
          className="btn-primary"
          onClick={async () => {
            await constructPositions();
            await fetchReturns({ allowAutoConstruct: false });
          }}
          disabled={loadingReturns || loadingPositions}
        >
          {loadingPositions || loadingReturns ? 'Calculating…' : 'Calculate positions & returns'}
        </button>
        <button className="btn-primary" style={{ marginLeft: 8 }} onClick={openSeedsManager}>Manage seeds</button>
        <button className="btn-primary" style={{ marginLeft: 8 }} onClick={() => setShowCA(true)}>Add corporate action</button>
        <button className="btn-primary" style={{ marginLeft: 8 }} onClick={startAuthorization}>Authorize with Schwab</button>
      </div>

      {/* Corporate Actions Modal */}
      {showCA && (
        <div className="modal-overlay">
          <div className="modal-window">
            <div className="modal-header">
              <h2 className="modal-title">Add Corporate Action</h2>
              <button className="modal-close" onClick={() => setShowCA(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-row" style={{ gridTemplateColumns: '140px 220px', maxWidth: 420, padding: '0 6px' }}>
                <label>Symbol:</label>
                <input value={caSymbol} onChange={(e)=>setCaSymbol(e.target.value.toUpperCase())} placeholder="e.g., GP" />
              </div>
              <div className="form-row" style={{ gridTemplateColumns: '140px 220px', maxWidth: 420, padding: '0 6px' }}>
                <label>Action type:</label>
                <select value={caType} onChange={(e)=>setCaType(e.target.value)}>
                  <option value="SPLIT">SPLIT</option>
                  <option value="REVERSE_SPLIT">REVERSE_SPLIT</option>
                </select>
              </div>
              <div className="form-row" style={{ gridTemplateColumns: '140px 100px 100px', maxWidth: 420, padding: '0 6px' }}>
                <label>Ratio (num/den):</label>
                <input type="number" step="0.0001" value={caNumerator} onChange={(e)=>setCaNumerator(e.target.value)} placeholder="1" />
                <input type="number" step="0.0001" value={caDenominator} onChange={(e)=>setCaDenominator(e.target.value)} placeholder="10" />
              </div>
              <div className="form-row" style={{ gridTemplateColumns: '140px 220px', maxWidth: 420, padding: '0 6px' }}>
                <label>Effective date:</label>
                <input type="date" value={caDate} onChange={(e)=>setCaDate(e.target.value)} />
              </div>
              <div className="form-row" style={{ gridTemplateColumns: '140px 360px', maxWidth: 540, padding: '0 6px' }}>
                <label>Notes:</label>
                <input value={caNotes} onChange={(e)=>setCaNotes(e.target.value)} placeholder="Optional" />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-toggle" onClick={() => setShowCA(false)}>Cancel</button>
              <button className="btn-primary" disabled={caSubmitting || !caSymbol || !caDate || !caNumerator || !caDenominator} onClick={async ()=>{
                try {
                  setCaSubmitting(true);
                  await axios.post('/api/invested_assets/corp_actions/create/',  {
                    symbol: (caSymbol||'').toUpperCase().trim(),
                    action_type: caType,
                    effective_date: caDate,
                    numerator: caNumerator,
                    denominator: caDenominator,
                    notes: caNotes,
                  });
                  alert('Corporate action saved. You may want to refetch historical prices for this symbol.');
                } catch(e) {
                  console.error('Failed to create corporate action', e);
                  alert('Failed to save corporate action.');
                } finally {
                  setCaSubmitting(false);
                }
              }}>{caSubmitting ? 'Saving…' : 'Save'}</button>
              <button className="btn-primary" disabled={caSubmitting || !caSymbol} onClick={async ()=>{
                try {
                  setCaSubmitting(true);
                  const sym = (caSymbol||'').toUpperCase().trim();
                  await axios.get(`/api/invested_assets/schwab/fetch_historical_prices/?symbols=${encodeURIComponent(sym )}&periodType=year&frequencyType=daily`);
                  alert(`Historical prices refetched for ${sym}.`);
                } catch(e) {
                  console.error('Failed to refetch prices', e);
                  alert('Failed to refetch prices.');
                } finally {
                  setCaSubmitting(false);
                }
              }}>Refetch prices</button>
            </div>
          </div>
        </div>
      )}

      {/* Seeds Manager Modal */}
      {showSeedsMgr && (
        <div className="modal-overlay">
          <div className="modal-window">
            <div className="modal-header">
              <h2 className="modal-title">{seedsView === 'list' ? 'Opening Seeds' : `Edit Seeds — ${editSeedDate}`}</h2>
              <button className="modal-close" onClick={closeSeedsManager}>×</button>
            </div>

            {/* Modal content */}
            <div className="modal-body">
              <div className="form-row" style={{ gridTemplateColumns: '140px 360px', maxWidth: 540, padding: '0 6px' }}>
                <label>Account:</label>
                <div>
                  {accountsLoading ? (
                    <span>Loading accounts…</span>
                  ) : (
                    <select value={seedsAccount} onChange={(e)=>setSeedsAccount(e.target.value)} style={{ width: '100%' }}>
                      <option value="">Select account…</option>
                      {accounts.map((a) => (
                        <option key={`${a.brokerage}-${a.account_number}`} value={a.account_number}>
                          {a.account_number} ({a.brokerage}{a.account_type ? ` • ${a.account_type}` : ''})
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
              {seedsView === 'list' && (
                <div className="table-actions">
                  <button className="btn-primary" onClick={() => { setEditSeedDate('2025-01-01'); setEditRows([{ symbol: 'CASH', quantity: '0' }]); setSeedsView('edit'); }}>
                    Add seeds…
                  </button>
                </div>
              )}

            {/* Body */}
            {seedsView === 'list' && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 8 }}>Click a date to edit its seeds. List is filtered by Account if provided. Seeds represent <strong>end-of-day</strong> positions.</div>
                <div style={{ border: '1px solid #374151', background: '#111827', borderRadius: 6 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '100px 140px 60px 120px 120px 70px', gap: 8, padding: 8, background: '#1f2937', borderBottom: '1px solid #374151', fontWeight: 600, fontSize: 10 }}>
                    <div>Date</div>
                    <div>Account</div>
                    <div>Count</div>
                    <div>Created</div>
                    <div>Last Edited</div>
                    <div>Actions</div>
                  </div>
                  {seedDates.length === 0 && (
                    <div style={{ padding: 12, color: '#9ca3af' }}>No seeds found.</div>
                  )}
                  {seedDates.map((row) => (
                    <div key={row.date} style={{ display: 'grid', gridTemplateColumns: '100px 140px 60px 120px 120px 70px', gap: 8, padding: '8px 8px', borderBottom: '1px solid #374151', alignItems: 'center', fontSize: 10 }}>
                      <div onClick={() => loadSeedsForDate(row.date, row.account)} style={{ cursor: 'pointer' }}>{row.date}</div>
                      <div onClick={() => loadSeedsForDate(row.date, row.account)} style={{ cursor: 'pointer', fontSize: 9 }}>
                        {row.brokerage} ({row.account?.slice(-4)})
                      </div>
                      <div onClick={() => loadSeedsForDate(row.date, row.account)} style={{ cursor: 'pointer' }}>{row.count}</div>
                      <div style={{ color: '#9ca3af' }}>{row.created_at ? new Date(row.created_at).toLocaleString([], {year:'numeric', month:'numeric', day:'numeric', hour:'2-digit', minute:'2-digit'}) : '-'}</div>
                      <div style={{ color: '#9ca3af' }}>{row.updated_at ? new Date(row.updated_at).toLocaleString([], {year:'numeric', month:'numeric', day:'numeric', hour:'2-digit', minute:'2-digit'}) : '-'}</div>
                      <div>
                        <button 
                          className="btn-toggle" 
                          style={{ padding: '2px 6px', color: '#f87171', borderColor: '#f87171', fontSize: '9px' }}
                          onClick={(e) => { e.stopPropagation(); deleteSeedsForDate(row.date); }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="modal-footer">
                  <button className="btn-toggle" onClick={closeSeedsManager}>Close</button>
                </div>
              </div>
            )}

            {seedsView === 'edit' && (
              <div style={{ marginTop: 12 }}>
                {/* Date picker at top of the edit form */}
                <div className="form-row" style={{ gridTemplateColumns: '140px 220px', maxWidth: 420, padding: '0 6px' }}>
                  <label>Seed date:</label>
                  <input type="date" value={editSeedDate} onChange={(e)=>setEditSeedDate(e.target.value)} />
                </div>
                <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 8 }}>CASH is always shown and valued at 1.0 (MV = quantity). Add symbols and quantities as of <strong>end-of-day</strong>.</div>
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 140px 80px', gap: 8, padding: '6px 0', borderBottom: '1px solid #374151', fontWeight: 600, fontSize: 11 }}>
                    <div>Symbol</div>
                    <div>Quantity</div>
                    <div>Last Edited</div>
                    <div></div>
                  </div>
                  {editRows.map((r, idx) => (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 140px 80px', gap: 8, padding: '6px 0', borderBottom: '1px solid #374151', alignItems: 'center', fontSize: 11 }}>
                      <input style={{ background: '#0f172a', border: '1px solid #374151', color: '#e5e7eb', borderRadius: 6, padding: '6px 8px', fontSize: 11 }} value={r.symbol} onChange={(e)=>updateEditRow(idx,'symbol',e.target.value)} placeholder="Symbol (e.g., CASH, SPYX)" />
                      <input style={{ background: '#0f172a', border: '1px solid #374151', color: '#e5e7eb', borderRadius: 6, padding: '6px 8px', fontSize: 11 }} value={r.quantity} onChange={(e)=>updateEditRow(idx,'quantity',e.target.value)} placeholder="Quantity" />
                      <div style={{ color: '#9ca3af', fontSize: 10 }}>{r.updated_at ? new Date(r.updated_at).toLocaleString() : '-'}</div>
                      <div>
                        <button className="btn-toggle small-btn" style={{ fontSize: 10, padding: '2px 6px' }} onClick={()=>removeEditRow(idx)} disabled={(r.symbol||'').toUpperCase()==='CASH'}>Remove</button>
                      </div>
                    </div>
                  ))}
                  <div style={{ marginTop: 8 }}>
                    <button className="btn-toggle small-btn" style={{ fontSize: 10, padding: '2px 8px' }} onClick={addEditRow}>+ Add row</button>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn-toggle" onClick={() => setSeedsView('list')}>Back</button>
                  <button className="btn-primary" onClick={submitSeeds} disabled={!editSeedDate || !seedsAccount.trim()}>Submit</button>
                  <button className="btn-toggle" onClick={closeSeedsManager}>Close</button>
                </div>
              </div>
            )}
            </div>
          </div>
        </div>
      )}

      {error && <div className="text-danger" style={{ marginTop: 8 }}>{error}</div>}

      {/* Benchmark Management Modal */}
      {showBenchmarkModal && (
        <div className="modal-overlay" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000
        }}>
          <div className="modal-content" style={{
            background: '#111827', color: '#e5e7eb', padding: 24, borderRadius: 12,
            width: '90%', maxWidth: 500, border: '1px solid #374151', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: 20, fontSize: 18, borderBottom: '1px solid #374151', paddingBottom: 10 }}>Manage Benchmark</h3>

            {bmError && <div style={{ color: '#ef4444', background: 'rgba(239,68,68,0.1)', padding: '8px 12px', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>{bmError}</div>}

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#9ca3af', marginBottom: 4 }}>Benchmark Name</label>
              <input
                style={{ width: '100%', background: '#0f172a', border: '1px solid #374151', color: '#e5e7eb', borderRadius: 6, padding: '8px 12px', fontSize: 14 }}
                value={bmModalData.name}
                onChange={(e) => setBmModalData({ ...bmModalData, name: e.target.value })}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <label style={{ fontSize: 12, color: '#9ca3af' }}>Constituents (Tickers & Weights)</label>
                <button
                  onClick={addBmConstituent}
                  style={{ background: '#3b82f6', color: 'white', border: 'none', borderRadius: 4, padding: '2px 8px', fontSize: 11, cursor: 'pointer' }}
                >
                  + Add
                </button>
              </div>

              <div style={{ maxHeight: 250, overflowY: 'auto', border: '1px solid #1f2937', borderRadius: 6, padding: 8, background: '#0b1220' }}>
                {bmModalData.constituents.map((c, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                    <input
                      placeholder="Symbol (e.g. SPY)"
                      style={{ flex: 2, background: '#0f172a', border: '1px solid #374151', color: '#e5e7eb', borderRadius: 6, padding: '6px 10px', fontSize: 13 }}
                      value={c.instrument}
                      onChange={(e) => updateBmConstituent(idx, 'instrument', e.target.value.toUpperCase())}
                    />
                    <input
                      type="number"
                      step="0.01"
                      placeholder="Weight (0-1)"
                      style={{ flex: 1, background: '#0f172a', border: '1px solid #374151', color: '#e5e7eb', borderRadius: 6, padding: '6px 10px', fontSize: 13 }}
                      value={c.weight}
                      onChange={(e) => updateBmConstituent(idx, 'weight', e.target.value)}
                    />
                    <button
                      onClick={() => removeBmConstituent(idx)}
                      style={{ background: 'transparent', color: '#9ca3af', border: 'none', cursor: 'pointer', padding: 4 }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                {bmModalData.constituents.length === 0 && (
                  <div style={{ textAlign: 'center', color: '#6b7280', fontSize: 13, padding: '20px 0' }}>No constituents defined</div>
                )}
              </div>

              <div style={{ marginTop: 8, fontSize: 12, textAlign: 'right', color: Math.abs(bmModalData.constituents.reduce((sum, c) => sum + parseFloat(c.weight || 0), 0) - 1.0) < 1e-6 ? '#10b981' : '#f59e0b' }}>
                Total Weight: {bmModalData.constituents.reduce((sum, c) => sum + parseFloat(c.weight || 0), 0).toFixed(4)}
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
              <button
                onClick={() => setShowBenchmarkModal(false)}
                style={{ background: 'transparent', color: '#e5e7eb', border: '1px solid #374151', borderRadius: 6, padding: '8px 16px', fontSize: 14, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleBenchmarkSave}
                disabled={bmLoading}
                style={{ background: '#3b82f6', color: 'white', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 14, fontWeight: 600, cursor: bmLoading ? 'not-allowed' : 'pointer', opacity: bmLoading ? 0.7 : 1 }}
              >
                {bmLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Returns charts */}
      {returnsData && (
        <div style={{ marginTop: 12 }}>
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
            <div>
              <strong>Portfolio TWR:</strong> {(returnsData.portfolio?.twr*100).toFixed(2)}% &nbsp; | &nbsp;
              <strong>Benchmark TWR:</strong> {(returnsData.benchmark?.twr*100).toFixed(2)}%
            </div>
            <button
              onClick={openBenchmarkModal}
              style={{
                background: '#1e293b',
                color: '#3b82f6',
                border: '1px solid #3b82f6',
                borderRadius: 4,
                padding: '2px 8px',
                fontSize: 12,
                cursor: 'pointer'
              }}
            >
              Manage Benchmark
            </button>
          </div>

          {/* Tabs Navigation */}
          <div style={{ display: 'flex', gap: 4, marginTop: 16, borderBottom: '1px solid #1f2937', paddingBottom: 0 }}>
            {[
              { id: 'returns', label: 'Returns' },
              { id: 'mv', label: 'Market Value' },
              { id: 'contribution', label: 'Contribution' },
              { id: 'tx_pos', label: 'Transactions & Positions' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '8px 16px',
                  background: activeTab === tab.id ? '#1e293b' : 'transparent',
                  color: activeTab === tab.id ? '#3b82f6' : '#9ca3af',
                  border: 'none',
                  borderBottom: activeTab === tab.id ? '2px solid #3b82f6' : '2px solid transparent',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: activeTab === tab.id ? 600 : 400,
                  transition: 'all 0.2s'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div style={{ marginTop: 16 }}>
            {/* Tab 1: Returns Graph */}
            {activeTab === 'returns' && (
              <div>
                {dailyReturnsChart()}
              </div>
            )}

            {/* Tab 2: Market Value Graph */}
            {activeTab === 'mv' && (
              <div>
                {portfolioMVFromReturnsChart()}
              </div>
            )}

            {/* Tab 3: Contribution Analysis Table */}
            {activeTab === 'contribution' && returnsData.portfolio?.position_contribution && (
              <div style={{ background: '#0b1220', border: '1px solid #1f2937', color: '#e5e7eb', borderRadius: 8, padding: 10 }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>Performance Contribution Analysis</div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: '#111827' }}>
                        <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #374151' }}>Symbol</th>
                        <th style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #374151' }}>Avg Weight</th>
                        <th style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #374151' }}>Return</th>
                        <th style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #374151' }}>Contribution</th>
                        <th style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #374151' }}>Rel to BM</th>
                      </tr>
                    </thead>
                    <tbody>
                      {returnsData.portfolio.position_contribution.map((pc, idx) => (
                        <tr key={pc.symbol + idx} style={{ borderBottom: '1px solid #1f2937' }}>
                          <td style={{ padding: '6px 8px', fontWeight: 500 }}>{pc.symbol}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right' }}>{(pc.avg_weight * 100).toFixed(2)}%</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', color: pc.return >= 0 ? '#10b981' : '#ef4444' }}>
                            {(pc.return * 100).toFixed(2)}%
                          </td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, color: pc.contribution >= 0 ? '#10b981' : '#ef4444' }}>
                            {(pc.contribution * 100).toFixed(2)}%
                          </td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', color: pc.relative_to_bm >= 0 ? '#10b981' : '#ef4444' }}>
                            {(pc.relative_to_bm * 100).toFixed(2)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Tab 4: Transactions & Positions Tables */}
            {activeTab === 'tx_pos' && (
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', top: -45, right: 0 }}>
                  <button
                    onClick={async () => {
                      try {
                        const params = new URLSearchParams({
                          start: startDate,
                          end: endDate,
                          portfolio_id: portfolioId,
                        });
                        const url = `/api/invested_assets/export/excel/?${params.toString()}`;
                        window.open(url, '_blank');
                      } catch (e) {
                        console.error('Failed to export Excel', e);
                        alert('Failed to export Excel.');
                      }
                    }}
                    style={{
                      background: '#059669',
                      color: 'white',
                      border: 'none',
                      borderRadius: 6,
                      padding: '6px 12px',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    Export to Excel
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, alignItems: 'start' }}>
                  {/* Transactions panel */}
                  <div style={{ background: '#0b1220', border: '1px solid #1f2937', color: '#e5e7eb', borderRadius: 8, padding: 10, display: 'flex', flexDirection: 'column', maxHeight: 600 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                      <div style={{ fontWeight: 600 }}>Transactions</div>
                      <input
                        value={txSearch}
                        onChange={(e)=> setTxSearch(e.target.value)}
                        placeholder="Search (symbol, account, type)"
                        style={{ flex: 1, background: '#0f172a', border: '1px solid #374151', color: '#e5e7eb', borderRadius: 6, padding: '6px 8px' }}
                      />
                    </div>
                    {txError && <div style={{ color: 'tomato', fontSize: 12, marginBottom: 6 }}>{txError}</div>}
                    <div style={{ overflowY: 'auto', flex: 1 }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <thead>
                          <tr style={{ position: 'sticky', top: 0, background: '#111827' }}>
                            {[
                              { key: 'date', label: 'Date' },
                              { key: 'time', label: 'Time' },
                              { key: 'account_number', label: 'Account' },
                              { key: 'transaction_type', label: 'Type' },
                              { key: 'symbol', label: 'Symbol' },
                              { key: 'quantity', label: 'Qty' },
                              { key: 'price_at_trade', label: 'Price' },
                              { key: 'net_amount', label: 'Net' },
                              { key: 'fees_total', label: 'Fees' },
                            ].map(col => (
                              <th key={col.key} onClick={()=>toggleSort(col.key)} style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #374151', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                {col.label}
                                {txSort.key === col.key ? (txSort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                              </th>
                            ))}
                          </tr>
                          <tr style={{ position: 'sticky', top: 28, background: '#111827' }}>
                            <th style={{ padding: 4 }}>
                              <input value={txFilters.date} onChange={e=>setTxFilter('date', e.target.value)} placeholder="YYYY-MM-DD" style={{ width: '100%', background: '#0f172a', border: '1px solid #374151', color: '#e5e7eb', borderRadius: 4, padding: '3px 6px' }} />
                            </th>
                            <th></th>
                            <th style={{ padding: 4 }}>
                              <input value={txFilters.account_number} onChange={e=>setTxFilter('account_number', e.target.value)} placeholder="acct" style={{ width: '100%', background: '#0f172a', border: '1px solid #374151', color: '#e5e7eb', borderRadius: 4, padding: '3px 6px' }} />
                            </th>
                            <th style={{ padding: 4 }}>
                              <input value={txFilters.transaction_type} onChange={e=>setTxFilter('transaction_type', e.target.value)} placeholder="type" style={{ width: '100%', background: '#0f172a', border: '1px solid #374151', color: '#e5e7eb', borderRadius: 4, padding: '3px 6px' }} />
                            </th>
                            <th style={{ padding: 4 }}>
                              <input value={txFilters.symbol} onChange={e=>setTxFilter('symbol', e.target.value)} placeholder="symbol" style={{ width: '100%', background: '#0f172a', border: '1px solid #374151', color: '#e5e7eb', borderRadius: 4, padding: '3px 6px' }} />
                            </th>
                            <th></th>
                            <th></th>
                            <th></th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {loadingTransactions ? (
                            <tr><td colSpan={9} style={{ padding: 12, color: '#9ca3af' }}>Loading…</td></tr>
                          ) : (
                            visibleTransactions.map((r, idx) => (
                              <tr key={`${r.activity_id}-${idx}`} style={{ borderBottom: '1px solid #1f2937' }}>
                                <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{r.date}</td>
                                <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{r.time}</td>
                                <td style={{ padding: '6px 8px' }}>{r.account_number}</td>
                                <td style={{ padding: '6px 8px' }}>{r.transaction_type}</td>
                                <td style={{ padding: '6px 8px' }}>{r.symbol || ''}</td>
                                <td style={{ padding: '6px 8px', textAlign: 'right' }}>{r.quantity != null ? Number(r.quantity).toLocaleString() : ''}</td>
                                <td style={{ padding: '6px 8px', textAlign: 'right' }}>{r.price_at_trade != null ? formatCurrency(r.price_at_trade) : ''}</td>
                                <td style={{ padding: '6px 8px', textAlign: 'right' }}>{r.net_amount != null ? formatCurrency(r.net_amount) : ''}</td>
                                <td style={{ padding: '6px 8px', textAlign: 'right' }}>{r.fees_total ? formatCurrency(r.fees_total) : ''}</td>
                              </tr>
                            ))
                          )}
                          {!loadingTransactions && visibleTransactions.length === 0 && (
                            <tr><td colSpan={9} style={{ padding: 12, color: '#9ca3af' }}>No transactions</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Positions panel */}
                  <div style={{ background: '#0b1220', border: '1px solid #1f2937', color: '#e5e7eb', borderRadius: 8, padding: 10, display: 'flex', flexDirection: 'column', maxHeight: 600 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                      <div style={{ fontWeight: 600 }}>Positions</div>
                      <input
                        value={posSearch}
                        onChange={(e)=> setPosSearch(e.target.value)}
                        placeholder="Search (symbol, account)"
                        style={{ flex: 1, background: '#0f172a', border: '1px solid #374151', color: '#e5e7eb', borderRadius: 6, padding: '6px 8px' }}
                      />
                    </div>
                    <div style={{ overflowY: 'auto', flex: 1 }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <thead>
                          <tr style={{ position: 'sticky', top: 0, background: '#111827' }}>
                            {[
                              { key: 'date', label: 'Date' },
                              { key: 'account', label: 'Account' },
                              { key: 'symbol', label: 'Symbol' },
                              { key: 'price', label: 'EOD Price' },
                              { key: 'qty', label: 'Qty' },
                              { key: 'mv', label: 'MV' },
                            ].map(col => (
                              <th key={col.key} onClick={()=>togglePosSort(col.key)} style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #374151', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                {col.label}
                                {posSort.key === col.key ? (posSort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                              </th>
                            ))}
                          </tr>
                          <tr style={{ position: 'sticky', top: 28, background: '#111827' }}>
                            <th style={{ padding: 4 }}>
                              <input value={posFilters.date} onChange={e=>setPosFilter('date', e.target.value)} placeholder="YYYY-MM-DD" style={{ width: '100%', background: '#0f172a', border: '1px solid #374151', color: '#e5e7eb', borderRadius: 4, padding: '3px 6px' }} />
                            </th>
                            <th style={{ padding: 4 }}>
                              <input value={posFilters.account} onChange={e=>setPosFilter('account', e.target.value)} placeholder="acct" style={{ width: '100%', background: '#0f172a', border: '1px solid #374151', color: '#e5e7eb', borderRadius: 4, padding: '3px 6px' }} />
                            </th>
                            <th style={{ padding: 4 }}>
                              <input value={posFilters.symbol} onChange={e=>setPosFilter('symbol', e.target.value)} placeholder="symbol" style={{ width: '100%', background: '#0f172a', border: '1px solid #374151', color: '#e5e7eb', borderRadius: 4, padding: '3px 6px' }} />
                            </th>
                            <th></th>
                            <th></th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {loadingPositions ? (
                            <tr><td colSpan={6} style={{ padding: 12, color: '#9ca3af' }}>Loading…</td></tr>
                          ) : (
                            visiblePositions.map((r, idx) => (
                              <tr key={`${r.date}-${r.account}-${r.symbol}-${idx}`} style={{ borderBottom: '1px solid #1f2937' }}>
                                <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{r.date}</td>
                                <td style={{ padding: '6px 8px' }}>{r.account}</td>
                                <td style={{ padding: '6px 8px' }}>{r.symbol || ''}</td>
                                <td style={{ padding: '6px 8px', textAlign: 'right' }}>{r.price != null ? formatCurrency(r.price) : ''}</td>
                                <td style={{ padding: '6px 8px', textAlign: 'right' }}>{r.qty != null ? Number(r.qty).toLocaleString() : ''}</td>
                                <td style={{ padding: '6px 8px', textAlign: 'right' }}>{formatCurrency(r.mv)}</td>
                              </tr>
                            ))
                          )}
                          {!loadingPositions && visiblePositions.length === 0 && (
                            <tr><td colSpan={6} style={{ padding: 12, color: '#9ca3af' }}>No positions</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default InvestedAssetDetail;